import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { BookOpen, Film, Tv, Gamepad2, Star, Users, Trophy } from "lucide-react";
import { useTheme } from "../components/ThemeProvider";
import { motion } from "framer-motion";
import axios from "axios";
import { useTranslation } from "../i18n";

const API = '/api';

const mediaIconDefs = [
  { icon: BookOpen, key: "books" },
  { icon: Film, key: "movies" },
  { icon: Tv, key: "tvSeries" },
  { icon: Gamepad2, key: "videoGames" },
];

const statDefs = [
  { icon: Star, key: "rate" },
  { icon: Trophy, key: "track" },
  { icon: Users, key: "review" },
];

export function Landing() {
  const { t } = useTranslation();
  const mediaIcons = mediaIconDefs.map(d => ({ icon: d.icon, title: t.landing.features[d.key].title }));
  const stats = statDefs.map(d => ({ icon: d.icon, value: t.landing.stats[d.key].value, label: t.landing.stats[d.key].label }));
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const horizontalLogo = isDark ? "/logo-horizontal-dark.svg" : "/logo-horizontal.svg";
  
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(() => {
        navigate("/dashboard", { replace: true });
      })
      .catch(() => {
        setChecking(false);
      });
  }, [navigate]);
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t.common.loading}</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="AdventureTrack!" className="h-7 w-7 object-contain" />
          </div>
          <Button onClick={handleLogin} className="btn-scale rounded-full" data-testid="login-button">
            {t.landing.signInGoogle}
          </Button>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <img src={horizontalLogo} alt="Adventure Track!" className="h-16 sm:h-20 mx-auto object-contain" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6"
          >
            {t.landing.heroTitle1}{" "}
            <span className="text-primary">{t.landing.heroTitle2}</span>{" "}
            {t.landing.heroTitle3}
          </motion.h1>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-2xl mx-auto mb-10"
          >
            <p className="text-lg text-muted-foreground">{t.landing.heroSubtitle1}</p>
            <p className="text-lg text-muted-foreground mt-1">{t.landing.heroSubtitle2}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button 
              size="lg" 
              onClick={handleLogin}
              className="btn-scale rounded-full text-base px-8 py-6"
              data-testid="hero-cta"
            >
              {t.landing.startAdventure}
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Features Section - Unified Card */}
      <section className="py-20 px-6 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-10">
            {t.landing.trackAllMedia}
          </h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-8 sm:p-10 rounded-2xl border border-border bg-card text-center"
            data-testid="features-card"
          >
            <div className="flex items-center justify-center gap-8 sm:gap-14 mb-6">
              {mediaIcons.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                  className="flex flex-col items-center gap-2"
                >
                  <item.icon className="w-10 h-10 text-primary" />
                  <span className="text-sm font-medium">{item.title}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">{t.landing.featuresCommon}</p>
          </motion.div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-20 px-6 bg-card border-y border-border">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-lg sm:text-xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">{t.landing.readyToStart}</h2>
          <p className="text-muted-foreground mb-8">
            {t.landing.joinToday}
          </p>
          <Button 
            size="lg" 
            onClick={handleLogin}
            className="btn-scale rounded-full text-base px-8"
            data-testid="footer-cta"
          >
            {t.landing.signUpGoogle}
          </Button>
        </div>
      </section>
      
      <footer className="py-8 px-6 border-t border-border text-center text-sm text-muted-foreground">
        <p>Adventure Track! &copy; {new Date().getFullYear()}. {t.landing.trackJourney}</p>
      </footer>
    </div>
  );
}

export default Landing;
